import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { Control, useForm, useWatch } from 'react-hook-form';
import CircuitModalBenefitSelectionTablesByCity, {
  BenefitFiltersType,
} from './CircuitModalBenefitSelectionTable';
import { PublishedBenefitsParams } from '@/api/benefit.api';
import { useMemo } from 'react';
import { getBenefitNameFromType } from '@/utils/benefits/getBenefitTypeName';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { useGetPublishedBenefits } from '@/hooks/useBenefits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import StyledCircuitModal from './StyledCircuitModal';
import { Label } from '@/components/ui/label';
import StyledInput from '@/components/styled/StyledInput';
import DisplaySelectionTable from './DisplaySelectionTable';
import StyledSelect from '@/components/styled/StyledSelect';
import { CIRCUIT_DAY_PERIODS } from '@/constants/circuitDayPeriods';
import HorizontalLine from '@/components/ui/HorizontalLine';
import SearchIcon from '@/assets/icons/search.svg?react';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import {
  getHotelCategoryLabel,
  getHotelCategoryName,
} from '@/utils/benefits/getHotelCategoryName';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { resolvePensionPricings } from '@/utils/estimates/hotel/computeHotelPensionPrice';

const HOTEL_PENSION_ORDER: HotelPensionType[] = [
  'SingleRoom',
  'Breakfast',
  'SemiPension',
  'FullPension',
  'AllInclusive',
];

const hasPositivePensionPrice = (price: {
  adultPrice?: number | null;
  childPrice?: number | null;
  adolescentPrice?: number | null;
}) =>
  (price.adultPrice ?? 0) > 0 ||
  (price.childPrice ?? 0) > 0 ||
  (price.adolescentPrice ?? 0) > 0;

const getAvailablePensions = (benefit: BenefitFormData) => {
  const set = new Set<HotelPensionType>();
  const basePensionType = benefit.hotel?.pricingPensionType as
    | HotelPensionType
    | undefined;
  if (basePensionType) set.add(basePensionType);

  const pensionPricings = resolvePensionPricings(
    benefit.hotel?.commonPensionPricings ?? [],
    benefit.hotel?.HotelRoomCategory?.flatMap(
      (category) => category.categoryPensionPricings ?? [],
    ) ?? [],
    benefit.hotel?.areOtherPensionPricesCommon ?? false,
  );

  pensionPricings.forEach((pricing) => {
    if (!pricing.periods?.length) return;
    pricing.prices?.forEach((price) => {
      if (hasPositivePensionPrice(price)) set.add(price.pensionType);
    });
  });

  return set;
};

const getSafePensionType = (
  benefit: BenefitFormData,
  requestedPensionType?: HotelPensionType,
) => {
  const available = getAvailablePensions(benefit);
  if (requestedPensionType && available.has(requestedPensionType)) {
    return requestedPensionType;
  }
  const base = benefit.hotel?.pricingPensionType as
    | HotelPensionType
    | undefined;
  if (base && available.has(base)) return base;
  return HOTEL_PENSION_ORDER.find((pensionType) => available.has(pensionType));
};

interface GenericCircuitBenefitAdditionModalProps {
  index: number;
  stepIndex: number;
  circuitControl: Control<CircuitFormDataWithTranslations>;
  addBenefits: ({
    standardBenefit,
    optionalBenefits,
    standardBenefitPensionType,
    optionalBenefitsPensionTypes,
    stepNbDays,
  }: {
    standardBenefit: BenefitFormData;
    optionalBenefits: BenefitFormData[];
    standardBenefitPensionType?: HotelPensionType;
    optionalBenefitsPensionTypes?: Record<string, HotelPensionType>;
    stepNbDays?: number;
  }) => void;
  standardBenefit?: BenefitFormData;
  optionalBenefits: Array<BenefitFormData>;
  standardBenefitPensionType?: HotelPensionType;
  optionalBenefitsPensionTypes?: Record<string, HotelPensionType>;
  closeModal: () => void;
  modalType: Exclude<CircuitBenefitType, 'Transport'>;
  isViewMode?: boolean;
}

export type CircuitBenefitAdditionModalProps = Omit<
  GenericCircuitBenefitAdditionModalProps,
  'modalType'
>;

const GenericCircuitBenefitAdditionModal = ({
  index,
  stepIndex,
  circuitControl,
  addBenefits,
  standardBenefit,
  optionalBenefits,
  standardBenefitPensionType,
  optionalBenefitsPensionTypes,
  closeModal,
  modalType,
  isViewMode = false,
}: GenericCircuitBenefitAdditionModalProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const circuitBenefitType = useMemo(() => {
    switch (modalType) {
      case 'Activités':
        return 'activities';
      case 'Repas':
        return 'meals';
      case 'Hébergements':
        return 'accommodations';
    }
  }, [modalType]);

  const benefitType = useMemo(() => {
    switch (modalType) {
      case 'Hébergements':
        return getBenefitNameFromType(BenefitType.HOTEL);
      default:
        return getBenefitNameFromType(BenefitType.ACTIVITY);
    }
  }, [modalType]);

  const activityType = useMemo(() => {
    switch (modalType) {
      case 'Activités':
        return getBenefitNameFromType(BenefitType.ACTIVITY);
      case 'Repas':
        return getBenefitNameFromType(BenefitType.MEAL);
      default:
        return undefined;
    }
  }, [modalType]);

  const modalTitle = useMemo(() => {
    switch (modalType) {
      case 'Hébergements':
        return t('circuits.editorial.modalTitles.addAccommodation');
      case 'Activités':
        return t('circuits.editorial.modalTitles.addActivity');
      case 'Repas':
        return t('circuits.editorial.modalTitles.addMeal');
    }
  }, [modalType, t]);

  const { control } = useForm<BenefitFiltersType>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      standardBenefit: standardBenefit,
      optionalBenefits: optionalBenefits,
      standardBenefitPensionType,
      optionalBenefitsPensionTypes: optionalBenefitsPensionTypes ?? {},
      hotelClassifications: [],
      hotelCity: undefined,
    },
  });

  const selectedStandardBenefit = useWatch({
    control,
    name: 'standardBenefit',
  });
  const selectedOptionalBenefits = useWatch({
    control,
    name: 'optionalBenefits',
  });
  const selectedStandardBenefitPensionType = useWatch({
    control,
    name: 'standardBenefitPensionType',
  });
  const selectedOptionalBenefitsPensionTypes = useWatch({
    control,
    name: 'optionalBenefitsPensionTypes',
  });
  const nameSearchValue = useWatch({ control, name: 'search' });
  const debouncedNameSearch = useDebouncedSearch(nameSearchValue);
  const hotelClassifications = useWatch({
    control,
    name: 'hotelClassifications',
  });
  const hotelCity = useWatch({ control, name: 'hotelCity' });
  const nbDays = useWatch({
    control: circuitControl,
    name: `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.nbDays`,
  });
  const selectedCountries = useWatch({
    control: circuitControl,
    name: `countries`,
  });

  const countries = useMemo(
    () => selectedCountries.map(({ country }) => country.value),
    [selectedCountries],
  );

  const benefitsFilters: PublishedBenefitsParams = useMemo(
    () => ({
      types: [benefitType],
      activityType,
      name: debouncedNameSearch,
      hotelClassifications: hotelClassifications?.map((classification) =>
        getHotelCategoryName(t, classification.value),
      ),
      hotelCity: hotelCity?.value,
      countries,
    }),
    [
      activityType,
      benefitType,
      countries,
      debouncedNameSearch,
      hotelCity?.value,
      hotelClassifications,
      t,
    ],
  );

  const { data: publishedBenefits } = useGetPublishedBenefits(benefitsFilters);

  const { data: hotelCategoriesOptions } = useGetPublishedBenefits(
    {
      ...benefitsFilters,
      hotelClassifications: undefined,
    },
    (data) =>
      Object.keys(data)
        .flatMap((city) => data[city])
        .filter((benefit) => benefit.hotel && benefit.hotel.category)
        .map((benefit) =>
          getHotelCategoryLabel(t, benefit.hotel?.category ?? ''),
        )
        .map((category) => ({
          value: category,
          label: category,
        })),
  );

  const hotelCities = useMemo(
    () =>
      Object.keys(publishedBenefits ?? {})
        .toSorted()
        .filter((city) => city !== 'null')
        .map((city) => ({ label: city, value: city })),
    [publishedBenefits],
  );

  const isModificationMode = useMemo(() => {
    return Boolean(standardBenefit || optionalBenefits.length > 0);
  }, [standardBenefit, optionalBenefits]);

  return (
    <StyledCircuitModal
      title={modalTitle}
      closeModal={closeModal}
      dialogClassName={
        modalType === 'Hébergements' ? 'w-[50%] 2xl:w-[40%]' : undefined
      }
      actionOnClick={() => {
        const selectedOptionals = (selectedOptionalBenefits ?? []).filter(
          (benefit) => benefit.id !== selectedStandardBenefit?.id,
        );
        const safeStandardPensionType = selectedStandardBenefit
          ? getSafePensionType(
              selectedStandardBenefit,
              selectedStandardBenefitPensionType ??
                (selectedStandardBenefit.hotel?.pricingPensionType as
                  | HotelPensionType
                  | undefined),
            )
          : undefined;
        const safeOptionalPensions = Object.fromEntries(
          selectedOptionals
            .map((benefit) => [
              benefit.id,
              getSafePensionType(
                benefit,
                selectedOptionalBenefitsPensionTypes?.[benefit.id] ??
                  (benefit.hotel?.pricingPensionType as
                    | HotelPensionType
                    | undefined),
              ),
            ])
            .filter((entry): entry is [string, HotelPensionType] =>
              Boolean(entry[1]),
            ),
        );

        addBenefits({
          standardBenefit: selectedStandardBenefit,
          optionalBenefits: selectedOptionals,
          standardBenefitPensionType: safeStandardPensionType,
          optionalBenefitsPensionTypes: safeOptionalPensions,
          stepNbDays: nbDays ?? 1,
        });
      }}
      actionTitle={
        isModificationMode
          ? t('circuits.editorial.modalActions.save')
          : t('circuits.editorial.modalActions.add')
      }
      isActionDisabled={isViewMode}
    >
      {modalType === 'Hébergements' && (
        <div className="flex gap-4 items-center">
          <Label className="text-gray-700">
            {t('circuits.editorial.numberOfNights')}
          </Label>
          <div>
            <StyledInput
              control={circuitControl}
              name={`circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.nbDays`}
              type="number"
              placeholder={t('circuits.editorial.numberOfNightsPlaceholder')}
              min={1}
              disabled={isViewMode}
            />
          </div>
        </div>
      )}
      <StyledInput
        control={control}
        name="search"
        placeholder={t('circuits.editorial.searchPlaceholder')}
        title={t('circuits.editorial.searchTitle')}
        icon={<SearchIcon className="size-5 stroke-gray-500" />}
      />
      {modalType === 'Hébergements' && (
        <StyledSelect
          control={control}
          name="hotelClassifications"
          options={hotelCategoriesOptions}
          title={t('circuits.editorial.hotelFilters.classification')}
          placeholder={t(
            'circuits.editorial.hotelFilters.classificationPlaceholder',
          )}
          isMulti
          disabled={isViewMode}
        />
      )}
      {modalType === 'Hébergements' && (
        <StyledSelect
          control={control}
          name="hotelCity"
          options={hotelCities ?? []}
          title={t('circuits.editorial.hotelFilters.city')}
          placeholder={t('circuits.editorial.hotelFilters.cityPlaceholder')}
          isClearable
          disabled={isViewMode}
        />
      )}
      <DisplaySelectionTable
        index={index}
        stepIndex={stepIndex}
        type={modalType}
        control={circuitControl}
        isViewMode={isViewMode}
      />
      <StyledSelect
        control={circuitControl}
        name={`circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.time`}
        options={getTranslatedConst(CIRCUIT_DAY_PERIODS)}
        title={t('circuits.editorial.period')}
        placeholder={t('circuits.editorial.periodPlaceholder')}
        isClearable
        disabled={isViewMode}
      />
      {publishedBenefits && (
        <>
          <HorizontalLine />
          <CircuitModalBenefitSelectionTablesByCity
            control={control}
            publishedBenefits={publishedBenefits}
            title={modalType}
            benefitType={
              modalType === 'Hébergements' ? BenefitType.HOTEL : undefined
            }
            isViewMode={isViewMode}
          />
        </>
      )}
    </StyledCircuitModal>
  );
};

export default GenericCircuitBenefitAdditionModal;
